import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { lazy, Suspense } from "react";
import { AuthProvider } from "./components/auth/AuthProvider";
import { ScrollToTop } from "./components/ScrollToTop";
import { InstallPrompt } from "./components/InstallPrompt";
import { ChatBubble } from "./components/ChatBubble";
import { ContextualActivityFeed } from "./components/ContextualActivityFeed";
import { CookieConsent } from "./components/CookieConsent";
import { useTracking } from "./hooks/useTracking";
import { PresenceTracker } from "./components/PresenceTracker";

// Critical pages - load immediately
import Index from "./pages/Index";
import Directory from "./pages/Directory";
import TrainerDirectory from "./pages/TrainerDirectory";
import TrainerDetail from "./pages/TrainerDetail";
import NotFound from "./pages/NotFound";

// Lazy load less critical pages for better initial bundle
const ListingDetail = lazy(() => import("./pages/ListingDetail"));
const CreateListing = lazy(() => import("./pages/CreateListing"));
const EditListing = lazy(() => import("./pages/EditListing"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const AdminUnified = lazy(() => import("./pages/AdminUnified"));
const Admin = lazy(() => import("./pages/Admin"));
const AdminCategories = lazy(() => import("./pages/AdminCategories"));
const AdminVideos = lazy(() => import("./pages/AdminVideos"));
const AdminArticles = lazy(() => import("./pages/AdminArticles"));
const AdminSettings = lazy(() => import("./pages/AdminSettings"));
const AdminBilling = lazy(() => import("./pages/AdminBilling"));
const AdminSalesAnalytics = lazy(() => import("./pages/AdminSalesAnalytics"));
const AdminRefundRequests = lazy(() => import("./pages/AdminRefundRequests"));
const SubscriptionManagement = lazy(() => import("./pages/SubscriptionManagement"));
const RefundRequests = lazy(() => import("./pages/RefundRequests"));
const BillingProfile = lazy(() => import("./pages/BillingProfile"));
const AdminChatConversations = lazy(() => import("./pages/AdminChatConversations"));
const AdminChatFeedback = lazy(() => import("./pages/AdminChatFeedback"));
const AdminNewsletters = lazy(() => import("./pages/AdminNewsletters"));
const AdminNewsletterNew = lazy(() => import("./pages/AdminNewsletterNew"));
const AdminNewsletterAnalytics = lazy(() => import("./pages/AdminNewsletterAnalytics"));
const AdminNewsletterAutomations = lazy(() => import("./pages/AdminNewsletterAutomations"));
const NewsletterSubscribe = lazy(() => import("./pages/NewsletterSubscribe"));
const AdminBulkThumbnails = lazy(() => import("./pages/AdminBulkThumbnails"));
const AdminFixListingImages = lazy(() => import("./pages/AdminFixListingImages"));
const AdminListings = lazy(() => import("./pages/AdminListings"));
const AdminLandingPages = lazy(() => import("./pages/AdminLandingPages"));
const AdminEvents = lazy(() => import("./pages/AdminEvents"));
const ScrapingStats = lazy(() => import("./pages/admin/ScrapingStats"));
const NewsFeeds = lazy(() => import("./pages/admin/NewsFeeds"));
const VideoGallery = lazy(() => import("./pages/VideoGallery"));
const VideoDetail = lazy(() => import("./pages/VideoDetail"));
const SubmitVideo = lazy(() => import("./pages/SubmitVideo"));
const ArticleList = lazy(() => import("./pages/ArticleList"));
const Article = lazy(() => import("./pages/Article"));
const Events = lazy(() => import("./pages/Events"));
const CreateEvent = lazy(() => import("./pages/CreateEvent"));
const EventDetail = lazy(() => import("./pages/EventDetail"));
const Store = lazy(() => import("./pages/Store"));
const HtmlSitemap = lazy(() => import("./pages/HtmlSitemap"));
const SearchResults = lazy(() => import("./pages/SearchResults"));
const SubmitListing = lazy(() => import("./pages/SubmitListing"));
const PremiumListing = lazy(() => import("./pages/PremiumListing"));
const PremiumUpgrade = lazy(() => import("./pages/PremiumUpgrade"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const EventSuccess = lazy(() => import("./pages/EventSuccess"));
const ManageCoupons = lazy(() => import("./pages/ManageCoupons"));
const AnalyticsDashboard = lazy(() => import("./pages/AnalyticsDashboard"));
const AIAnalyticsDashboard = lazy(() => import("./pages/AIAnalyticsDashboard"));
const UserBilling = lazy(() => import("./pages/UserBilling"));
const ChatAssistant = lazy(() => import("./pages/ChatAssistant"));
const LlmDatasetProxy = lazy(() => import("./pages/LlmDatasetProxy"));
const InstallApp = lazy(() => import("./pages/InstallApp"));
const BecomeTrainer = lazy(() => import("./pages/BecomeTrainer"));
const BecomePhysiotherapist = lazy(() => import("./pages/BecomePhysiotherapist"));
const BecomeNutritionist = lazy(() => import("./pages/BecomeNutritionist"));
const PhysiotherapistOnboarding = lazy(() => import("./pages/PhysiotherapistOnboarding"));
const NutritionistOnboarding = lazy(() => import("./pages/NutritionistOnboarding"));
const NutritionistDirectory = lazy(() => import("./pages/NutritionistDirectory"));
const PhysiotherapistDirectory = lazy(() => import("./pages/PhysiotherapistDirectory"));
const NutritionistDetail = lazy(() => import("./pages/NutritionistDetail"));
const PhysiotherapistDetail = lazy(() => import("./pages/PhysiotherapistDetail"));
const StoreDetail = lazy(() => import("./pages/StoreDetail"));
const Tribus = lazy(() => import("./pages/Tribus"));
const TribuDetail = lazy(() => import("./pages/TribuDetail"));
const TribusDesafios = lazy(() => import("./pages/TribusDesafios"));
const DesafioDetail = lazy(() => import("./pages/DesafioDetail"));
const TrainerSuccess = lazy(() => import("./pages/TrainerSuccess"));
const EditTrainerProfile = lazy(() => import("./pages/EditTrainerProfile"));
const EditNutritionistProfile = lazy(() => import("./pages/EditNutritionistProfile"));
const EditPhysiotherapistProfile = lazy(() => import("./pages/EditPhysiotherapistProfile"));
const TrainerBookingDashboard = lazy(() => import("./pages/TrainerBookingDashboard"));
const TrainerServicesPage = lazy(() => import("./pages/TrainerServicesPage"));
const TrainerAvailabilityPage = lazy(() => import("./pages/TrainerAvailabilityPage"));
const BookingSubscriptionPage = lazy(() => import("./pages/BookingSubscriptionPage"));
const BookingSystemLanding = lazy(() => import("./pages/BookingSystemLanding"));
const UniversalBookingDashboard = lazy(() => import("./pages/UniversalBookingDashboard"));
const UniversalServicesPage = lazy(() => import("./pages/UniversalServicesPage"));
const UniversalAvailabilityPage = lazy(() => import("./pages/UniversalAvailabilityPage"));
const AffiliateDashboard = lazy(() => import("./pages/AffiliateDashboard"));
const AffiliateRegister = lazy(() => import("./pages/AffiliateRegister"));
const AffiliateLeaderboard = lazy(() => import("./pages/AffiliateLeaderboard"));
const AffiliateTest = lazy(() => import("./pages/AffiliateTest"));
const AdminAffiliates = lazy(() => import("./pages/AdminAffiliates"));
const AdminListingClaims = lazy(() => import("./pages/AdminListingClaims"));
const AdminPromotionalBlocks = lazy(() => import("./pages/AdminPromotionalBlocks"));
const AdminActivityFeed = lazy(() => import("./pages/AdminActivityFeed"));
const AdminCookieAudit = lazy(() => import("./pages/AdminCookieAudit"));
const ClaimListing = lazy(() => import("./pages/ClaimListing"));
const ListingMessages = lazy(() => import("./pages/ListingMessages"));
const ServiciosProfesionales = lazy(() => import("./pages/ServiciosProfesionales"));
const OnboardingCenter = lazy(() => import("./pages/OnboardingCenter"));
const FAQ = lazy(() => import("./pages/FAQ"));
const About = lazy(() => import("./pages/About"));
const MetodosPago = lazy(() => import("./pages/MetodosPago"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const RefundPolicy = lazy(() => import("./pages/RefundPolicy"));
const LeadsDashboard = lazy(() => import("./pages/LeadsDashboard"));
const BuyLeads = lazy(() => import("./pages/BuyLeads"));
const CentrosBienestar = lazy(() => import("./pages/CentrosBienestar"));
const Explore = lazy(() => import("./pages/Explore"));
const NearMe = lazy(() => import("./pages/NearMe"));
const TiendasProveedores = lazy(() => import("./pages/TiendasProveedores"));
const StoreRegistrationForm = lazy(() => import("./pages/StoreRegistrationForm"));
const MusicCore = lazy(() => import("./pages/MusicCore"));
const CityActivity = lazy(() => import("./pages/CityActivity"));
const CityLanding = lazy(() => import("./pages/CityLanding"));
const LosMejoresLanding = lazy(() => import("./pages/LosMejoresLanding"));
const SportCategoryProfessionals = lazy(() => import("./pages/SportCategoryProfessionals"));
const SportProfessionalDetail = lazy(() => import("./pages/SportProfessionalDetail"));
const SportProfessionalRegister = lazy(() => import("./pages/SportProfessionalRegister"));
const SportProfessionalSuccess = lazy(() => import("./pages/SportProfessionalSuccess"));
const CreateSportProfile = lazy(() => import("./pages/CreateSportProfile"));
const RegisterDance = lazy(() => import("./pages/RegisterDance"));
const RegisterDanceSuccess = lazy(() => import("./pages/RegisterDanceSuccess"));

// Business landing pages - lazy loaded
const LandingYoga = lazy(() => import("./pages/LandingYoga"));
const LandingCrossfit = lazy(() => import("./pages/LandingCrossfit"));
const LandingFitness = lazy(() => import("./pages/LandingFitness"));
const LandingPilates = lazy(() => import("./pages/LandingPilates"));
const LandingDance = lazy(() => import("./pages/LandingDance"));
const LandingMartialArts = lazy(() => import("./pages/LandingMartialArts"));

// Business detail page
const BusinessDetail = lazy(() => import("./pages/BusinessDetail"));

// New pages for missing routes
const TrainerOnboarding = lazy(() => import("./pages/TrainerOnboarding"));
const BecomeStore = lazy(() => import("./pages/BecomeStore"));

// Sales funnel pages - lazy loaded
const FunnelTrainer = lazy(() => import("./pages/FunnelTrainer"));
const FunnelNutritionist = lazy(() => import("./pages/FunnelNutritionist"));
const FunnelPhysiotherapist = lazy(() => import("./pages/FunnelPhysiotherapist"));
const FunnelYoga = lazy(() => import("./pages/FunnelYoga"));
const FunnelCrossfit = lazy(() => import("./pages/FunnelCrossfit"));
const FunnelGym = lazy(() => import("./pages/FunnelGym"));
const FunnelPilates = lazy(() => import("./pages/FunnelPilates"));
const FunnelDance = lazy(() => import("./pages/FunnelDance"));
const FunnelMartialArts = lazy(() => import("./pages/FunnelMartialArts"));
const BookingSystemFunnel = lazy(() => import("./pages/BookingSystemFunnel"));

const queryClient = new QueryClient();

// Loading fallback for lazy components
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

// Provider type mapping for dynamic routes
const PROVIDER_TYPES = ["nutricionista", "fisioterapeuta", "yoga", "crossfit", "gym", "pilates", "dance", "martial_arts"] as const;
type ProviderType = "nutritionist" | "physiotherapist" | "yoga" | "crossfit" | "gym" | "pilates" | "dance" | "martial_arts";

const providerTypeMap: Record<string, ProviderType> = {
  nutricionista: "nutritionist",
  fisioterapeuta: "physiotherapist",
  yoga: "yoga",
  crossfit: "crossfit",
  gym: "gym",
  pilates: "pilates",
  dance: "dance",
  martial_arts: "martial_arts",
};

// Dynamic provider dashboard wrapper
const ProviderDashboard = ({ type, component }: { type: string; component: "booking" | "services" | "availability" }) => {
  const providerType = providerTypeMap[type];
  if (!providerType) return <NotFound />;
  
  switch (component) {
    case "booking":
      return <UniversalBookingDashboard providerType={providerType} />;
    case "services":
      return <UniversalServicesPage providerType={providerType} />;
    case "availability":
      return <UniversalAvailabilityPage providerType={providerType} />;
  }
};

const App = () => {
  useTracking();

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <CookieConsent />
        <BrowserRouter>
          <AuthProvider>
            <ScrollToTop />
            <InstallPrompt />
            <ChatBubble />
            <ContextualActivityFeed />
            <PresenceTracker />
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Core pages */}
                <Route path="/" element={<Index />} />
                
                {/* Directory - Spanish alias */}
                <Route path="/directory" element={<Directory />} />
                <Route path="/directorio" element={<Navigate to="/directory" replace />} />
                
                {/* Listings */}
                <Route path="/listing/:slug" element={<ListingDetail />} />
                <Route path="/listing/:id/edit" element={<EditListing />} />
                <Route path="/create-listing" element={<CreateListing />} />
                <Route path="/submit-listing" element={<SubmitListing />} />
                <Route path="/premium/:id" element={<PremiumListing />} />
                <Route path="/premium-upgrade" element={<PremiumUpgrade />} />
                <Route path="/payment-success" element={<PaymentSuccess />} />
                <Route path="/event-success" element={<EventSuccess />} />
                
                {/* User Dashboard */}
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/manage-coupons" element={<ManageCoupons />} />
                <Route path="/listing-messages" element={<ListingMessages />} />
                <Route path="/analytics" element={<AnalyticsDashboard />} />
                <Route path="/ai-analytics" element={<AIAnalyticsDashboard />} />
                <Route path="/billing" element={<UserBilling />} />
                <Route path="/subscription-management" element={<SubscriptionManagement />} />
                <Route path="/refund-requests" element={<RefundRequests />} />
                <Route path="/billing-profile" element={<BillingProfile />} />
                
                {/* Admin routes */}
                <Route path="/admin" element={<AdminUnified />} />
                <Route path="/admin/extractor" element={<Admin />} />
                <Route path="/admin/listings" element={<AdminListings />} />
                <Route path="/admin/categories" element={<AdminCategories />} />
                <Route path="/admin/videos" element={<AdminVideos />} />
                <Route path="/admin/articles" element={<AdminArticles />} />
                <Route path="/admin/settings" element={<AdminSettings />} />
                <Route path="/admin/billing" element={<AdminBilling />} />
                <Route path="/admin/sales-analytics" element={<AdminSalesAnalytics />} />
                <Route path="/admin/refund-requests" element={<AdminRefundRequests />} />
                <Route path="/admin/chat" element={<AdminChatConversations />} />
                <Route path="/admin/chat-feedback" element={<AdminChatFeedback />} />
                <Route path="/admin/newsletters" element={<AdminNewsletters />} />
                <Route path="/admin/newsletters/new" element={<AdminNewsletterNew />} />
                <Route path="/admin/newsletters/:id/analytics" element={<AdminNewsletterAnalytics />} />
                <Route path="/admin/newsletters/automations" element={<AdminNewsletterAutomations />} />
                <Route path="/admin/news-feeds" element={<NewsFeeds />} />
                <Route path="/admin/bulk-thumbnails" element={<AdminBulkThumbnails />} />
                <Route path="/admin/fix-listing-images" element={<AdminFixListingImages />} />
                <Route path="/admin/analytics" element={<AnalyticsDashboard />} />
                <Route path="/admin/ai-analytics" element={<AIAnalyticsDashboard />} />
                <Route path="/admin/landing-pages" element={<AdminLandingPages />} />
                <Route path="/admin/affiliates" element={<AdminAffiliates />} />
                <Route path="/admin/claims" element={<AdminListingClaims />} />
                <Route path="/admin/promotional-blocks" element={<AdminPromotionalBlocks />} />
                <Route path="/admin/activity-feed" element={<AdminActivityFeed />} />
                <Route path="/admin/cookie-audit" element={<AdminCookieAudit />} />
                <Route path="/admin/events" element={<AdminEvents />} />
                <Route path="/admin/scraping-stats" element={<ScrapingStats />} />
                
                {/* Content pages */}
                <Route path="/videos" element={<VideoGallery />} />
                <Route path="/video/:slug" element={<VideoDetail />} />
                <Route path="/submit-video" element={<SubmitVideo />} />
                <Route path="/articles" element={<ArticleList />} />
                <Route path="/articulos" element={<Navigate to="/articles" replace />} />
                <Route path="/article/:slug" element={<Article />} />
                <Route path="/articulo/:slug" element={<Article />} />
                
                {/* Events */}
                <Route path="/events" element={<Events />} />
                <Route path="/eventos" element={<Navigate to="/events" replace />} />
                <Route path="/create-event" element={<CreateEvent />} />
                <Route path="/event/:idOrSlug" element={<EventDetail />} />
                
                {/* Newsletter */}
                <Route path="/newsletter/subscribe" element={<NewsletterSubscribe />} />
                
                {/* Store */}
                <Route path="/store" element={<Store />} />
                <Route path="/stores" element={<Navigate to="/store" replace />} />
                <Route path="/tiendas" element={<Navigate to="/store" replace />} />
                <Route path="/store/:slug" element={<StoreDetail />} />
                <Route path="/tienda/:slug" element={<StoreDetail />} />
                
                {/* Utility pages */}
                <Route path="/search" element={<SearchResults />} />
                <Route path="/chat" element={<ChatAssistant />} />
                <Route path="/chat-assistant" element={<Navigate to="/chat" replace />} />
                <Route path="/llm-dataset.txt" element={<LlmDatasetProxy />} />
                <Route path="/llm-dataset" element={<LlmDatasetProxy />} />
                <Route path="/install" element={<InstallApp />} />
                
                {/* Professional onboarding */}
                <Route path="/become-trainer" element={<BecomeTrainer />} />
                <Route path="/become-physiotherapist" element={<BecomePhysiotherapist />} />
                <Route path="/become-nutritionist" element={<BecomeNutritionist />} />
                <Route path="/become-store" element={<BecomeStore />} />
                <Route path="/physiotherapist-onboarding" element={<PhysiotherapistOnboarding />} />
                <Route path="/nutritionist-onboarding" element={<NutritionistOnboarding />} />
                <Route path="/trainer-onboarding" element={<TrainerOnboarding />} />
                <Route path="/trainer-success" element={<TrainerSuccess />} />
                <Route path="/edit-trainer-profile" element={<EditTrainerProfile />} />
                <Route path="/edit-nutritionist-profile" element={<EditNutritionistProfile />} />
                <Route path="/edit-physiotherapist-profile" element={<EditPhysiotherapistProfile />} />
                
                {/* Professional directories */}
                <Route path="/trainers" element={<TrainerDirectory />} />
                <Route path="/entrenadores" element={<Navigate to="/trainers" replace />} />
                <Route path="/nutritionists" element={<NutritionistDirectory />} />
                <Route path="/nutricionistas" element={<Navigate to="/nutritionists" replace />} />
                <Route path="/physiotherapists" element={<PhysiotherapistDirectory />} />
                <Route path="/fisioterapeutas" element={<Navigate to="/physiotherapists" replace />} />
                
                {/* Professional profiles */}
                <Route path="/trainer/:slug" element={<TrainerDetail />} />
                <Route path="/entrenador/:slug" element={<TrainerDetail />} />
                <Route path="/nutritionist/:slug" element={<NutritionistDetail />} />
                <Route path="/nutricionista/:slug" element={<NutritionistDetail />} />
                <Route path="/physiotherapist/:slug" element={<PhysiotherapistDetail />} />
                <Route path="/fisioterapeuta/:slug" element={<PhysiotherapistDetail />} />
                
                {/* Business profile detail pages */}
                <Route path="/yoga/:slug" element={<BusinessDetail businessType="yoga" />} />
                <Route path="/crossfit/:slug" element={<BusinessDetail businessType="crossfit" />} />
                <Route path="/gym/:slug" element={<BusinessDetail businessType="gym" />} />
                <Route path="/pilates/:slug" element={<BusinessDetail businessType="pilates" />} />
                <Route path="/dance/:slug" element={<BusinessDetail businessType="dance" />} />
                <Route path="/martial-arts/:slug" element={<BusinessDetail businessType="martial_arts" />} />
                
                {/* Trainer booking dashboard */}
                <Route path="/dashboard/reservas" element={<TrainerBookingDashboard />} />
                <Route path="/dashboard/servicios" element={<TrainerServicesPage />} />
                <Route path="/dashboard/disponibilidad" element={<TrainerAvailabilityPage />} />
                <Route path="/dashboard/reservas/suscripcion" element={<BookingSubscriptionPage />} />
                
                {/* Provider-specific booking dashboards */}
                <Route path="/dashboard/nutricionista/reservas" element={<UniversalBookingDashboard providerType="nutritionist" />} />
                <Route path="/dashboard/nutricionista/servicios" element={<UniversalServicesPage providerType="nutritionist" />} />
                <Route path="/dashboard/nutricionista/disponibilidad" element={<UniversalAvailabilityPage providerType="nutritionist" />} />
                <Route path="/dashboard/fisioterapeuta/reservas" element={<UniversalBookingDashboard providerType="physiotherapist" />} />
                <Route path="/dashboard/fisioterapeuta/servicios" element={<UniversalServicesPage providerType="physiotherapist" />} />
                <Route path="/dashboard/fisioterapeuta/disponibilidad" element={<UniversalAvailabilityPage providerType="physiotherapist" />} />
                <Route path="/dashboard/yoga/reservas" element={<UniversalBookingDashboard providerType="yoga" />} />
                <Route path="/dashboard/yoga/servicios" element={<UniversalServicesPage providerType="yoga" />} />
                <Route path="/dashboard/yoga/disponibilidad" element={<UniversalAvailabilityPage providerType="yoga" />} />
                <Route path="/dashboard/crossfit/reservas" element={<UniversalBookingDashboard providerType="crossfit" />} />
                <Route path="/dashboard/crossfit/servicios" element={<UniversalServicesPage providerType="crossfit" />} />
                <Route path="/dashboard/crossfit/disponibilidad" element={<UniversalAvailabilityPage providerType="crossfit" />} />
                <Route path="/dashboard/gym/reservas" element={<UniversalBookingDashboard providerType="gym" />} />
                <Route path="/dashboard/gym/servicios" element={<UniversalServicesPage providerType="gym" />} />
                <Route path="/dashboard/gym/disponibilidad" element={<UniversalAvailabilityPage providerType="gym" />} />
                <Route path="/dashboard/pilates/reservas" element={<UniversalBookingDashboard providerType="pilates" />} />
                <Route path="/dashboard/pilates/servicios" element={<UniversalServicesPage providerType="pilates" />} />
                <Route path="/dashboard/pilates/disponibilidad" element={<UniversalAvailabilityPage providerType="pilates" />} />
                <Route path="/dashboard/dance/reservas" element={<UniversalBookingDashboard providerType="dance" />} />
                <Route path="/dashboard/dance/servicios" element={<UniversalServicesPage providerType="dance" />} />
                <Route path="/dashboard/dance/disponibilidad" element={<UniversalAvailabilityPage providerType="dance" />} />
                <Route path="/dashboard/martial_arts/reservas" element={<UniversalBookingDashboard providerType="martial_arts" />} />
                <Route path="/dashboard/martial_arts/servicios" element={<UniversalServicesPage providerType="martial_arts" />} />
                <Route path="/dashboard/martial_arts/disponibilidad" element={<UniversalAvailabilityPage providerType="martial_arts" />} />
                
                {/* Booking system */}
                <Route path="/sistema-reservas" element={<BookingSystemLanding />} />
                
                {/* Tribus */}
                <Route path="/tribus" element={<Tribus />} />
                <Route path="/tribus/:slug" element={<TribuDetail />} />
                <Route path="/tribus/:slug/desafios" element={<TribusDesafios />} />
                <Route path="/desafios/:id" element={<DesafioDetail />} />
                
                {/* Other pages */}
                <Route path="/servicios-profesionales" element={<ServiciosProfesionales />} />
                <Route path="/music-core" element={<MusicCore />} />
                <Route path="/onboarding" element={<OnboardingCenter />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/metodos-pago" element={<MetodosPago />} />
                <Route path="/terminos" element={<TermsOfService />} />
                <Route path="/privacidad" element={<PrivacyPolicy />} />
                <Route path="/devoluciones" element={<RefundPolicy />} />
                <Route path="/dashboard/leads" element={<LeadsDashboard />} />
                <Route path="/comprar-leads" element={<BuyLeads />} />
                <Route path="/centros-bienestar" element={<CentrosBienestar />} />
                <Route path="/claim-listing" element={<ClaimListing />} />
                
                {/* Affiliate */}
                <Route path="/affiliate/dashboard" element={<AffiliateDashboard />} />
                <Route path="/affiliate/register" element={<AffiliateRegister />} />
                <Route path="/affiliate/test" element={<AffiliateTest />} />
                <Route path="/affiliate/leaderboard" element={<AffiliateLeaderboard />} />
                
                {/* Explore */}
                <Route path="/explore" element={<Explore />} />
                <Route path="/explorar" element={<Navigate to="/explore" replace />} />
                <Route path="/cerca-de-mi" element={<NearMe />} />
                <Route path="/tiendas-proveedores" element={<TiendasProveedores />} />
                <Route path="/store-registration" element={<StoreRegistrationForm />} />
                
                {/* Los Mejores */}
                <Route path="/los-mejores" element={<LosMejoresLanding />} />
                <Route path="/los-mejores/:category" element={<SportCategoryProfessionals />} />
                <Route path="/los-mejores/:category/:slug" element={<SportProfessionalDetail />} />
                <Route path="/register" element={<SportProfessionalRegister />} />
                <Route path="/sport-professional-register" element={<Navigate to="/register" replace />} />
                <Route path="/sport-professional-success" element={<SportProfessionalSuccess />} />
                <Route path="/create-sport-profile" element={<CreateSportProfile />} />
                
                {/* Dance registration */}
                <Route path="/register-dance" element={<RegisterDance />} />
                <Route path="/register-dance-success" element={<RegisterDanceSuccess />} />
                
                {/* Business landing pages */}
                <Route path="/landing-yoga" element={<LandingYoga />} />
                <Route path="/landing-crossfit" element={<LandingCrossfit />} />
                <Route path="/landing-fitness" element={<LandingFitness />} />
                <Route path="/landing-pilates" element={<LandingPilates />} />
                <Route path="/landing-dance" element={<LandingDance />} />
                <Route path="/landing-martial-arts" element={<LandingMartialArts />} />
                
                {/* Sales funnels */}
                <Route path="/funnel/entrenador" element={<FunnelTrainer />} />
                <Route path="/funnel/trainer" element={<Navigate to="/funnel/entrenador" replace />} />
                <Route path="/funnel/nutricionista" element={<FunnelNutritionist />} />
                <Route path="/funnel/nutritionist" element={<Navigate to="/funnel/nutricionista" replace />} />
                <Route path="/funnel/fisioterapeuta" element={<FunnelPhysiotherapist />} />
                <Route path="/funnel/physiotherapist" element={<Navigate to="/funnel/fisioterapeuta" replace />} />
                <Route path="/funnel/yoga" element={<FunnelYoga />} />
                <Route path="/funnel/crossfit" element={<FunnelCrossfit />} />
                <Route path="/funnel/gym" element={<FunnelGym />} />
                <Route path="/funnel/gimnasio" element={<Navigate to="/funnel/gym" replace />} />
                <Route path="/funnel/pilates" element={<FunnelPilates />} />
                <Route path="/funnel/dance" element={<FunnelDance />} />
                <Route path="/funnel/baile" element={<Navigate to="/funnel/dance" replace />} />
                <Route path="/funnel/martial-arts" element={<FunnelMartialArts />} />
                <Route path="/funnel/artes-marciales" element={<Navigate to="/funnel/martial-arts" replace />} />
                <Route path="/funnel/reservas" element={<BookingSystemFunnel />} />
                <Route path="/funnel/booking-system" element={<Navigate to="/funnel/reservas" replace />} />
                
                {/* About and Sitemap */}
                <Route path="/about" element={<About />} />
                <Route path="/sitemap" element={<HtmlSitemap />} />
                <Route path="/ciudad/:citySlug" element={<CityLanding />} />
                <Route path="/:citySlug/:activitySlug" element={<CityActivity />} />
                
                {/* 404 */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
